import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import Login from './pages/Login';
import QueryOrder from './pages/QueryOrder';
import MainLayout from './components/MainLayout';
import OrderList from './pages/OrderList';
import OrderDetail from './pages/OrderDetail';
import OrderImport from './pages/OrderImport';
import UserManage from './pages/UserManage';
import UserImport from './pages/UserImport';
import PermissionTemplate from './pages/PermissionTemplate';
import SupplierManage from './pages/SupplierManage';
import FieldPermission from './pages/FieldPermission';
import StatusManage from './pages/StatusManage';
import TeamManage from './pages/TeamManage';
import GrowthMethodManage from './pages/GrowthMethodManage';
import PlatformManage from './pages/PlatformManage';
import OperationLog from './pages/OperationLog';
import Profile from './pages/Profile';
import Dashboard from './pages/Dashboard';
import DailyDashboard from './pages/DailyDashboard';
import SettlementSupplier from './pages/SettlementSupplier';
import SettlementOverall from './pages/SettlementOverall';
import SettlementProfit from './pages/SettlementProfit';
import ApiConfig from './pages/ApiConfig';
import ApiCallLogs from './pages/ApiCallLogs';
import BillInvoice from './pages/BillInvoice';
import ApiOpenEndpoints from './pages/ApiOpenEndpoints';
import ApiOpenKeys from './pages/ApiOpenKeys';
import ApiOpenLogs from './pages/ApiOpenLogs';
import ApiStrategy from './pages/ApiStrategy';
import ApiOrder from './pages/ApiOrder';
import ApiOrderList from './pages/ApiOrderList';
import EffectiveFans from './pages/EffectiveFans';
import SupplierPriceManage from './pages/SupplierPriceManage';
import FanMonitor from './pages/FanMonitor';
import ScheduledTasks from './pages/ScheduledTasks';

// 路由守卫
function PrivateRoute({ children }) {
  const token = localStorage.getItem('token');
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

// 系统管理员路由守卫
function SuperAdminRoute({ children }) {
  const token = localStorage.getItem('token');
  if (!token) return <Navigate to="/login" replace />;
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  if (user.username !== 'admin') return <Navigate to="/orders" replace />;
  return children;
}

// 菜单权限路由守卫：无对应菜单可见权限时跳回订单列表
function MenuRoute({ menuKey, children }) {
  const token = localStorage.getItem('token');
  if (!token) return <Navigate to="/login" replace />;
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  if (user.username === 'admin') return children;
  const perms = user.menu_permissions || [];
  const visible = perms.some(p => p.key === menuKey && p.is_visible === 1);
  if (!visible) return <Navigate to="/orders" replace />;
  return children;
}

function App() {
  return (
    <ConfigProvider locale={zhCN} theme={{ token: { colorPrimary: '#1677ff' } }}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/query" element={<QueryOrder />} />
          <Route path="/" element={
            <PrivateRoute>
              <MainLayout />
            </PrivateRoute>
          }>
            <Route index element={<Navigate to="/orders" replace />} />
            <Route path="orders" element={<OrderList />} />
            <Route path="orders/:id" element={<OrderDetail />} />
            <Route path="orders/import" element={<MenuRoute menuKey="/orders/import"><OrderImport /></MenuRoute>} />
            <Route path="users" element={<MenuRoute menuKey="/users"><UserManage /></MenuRoute>} />
            <Route path="users/import" element={<MenuRoute menuKey="/users"><UserImport /></MenuRoute>} />
            <Route path="permission-templates" element={<MenuRoute menuKey="/permission-templates"><PermissionTemplate /></MenuRoute>} />
            <Route path="suppliers" element={<MenuRoute menuKey="/suppliers"><SupplierManage /></MenuRoute>} />
            <Route path="suppliers/:id/fields" element={<MenuRoute menuKey="/suppliers"><FieldPermission /></MenuRoute>} />
            <Route path="statuses" element={<MenuRoute menuKey="/statuses"><StatusManage /></MenuRoute>} />
            <Route path="teams" element={<MenuRoute menuKey="/teams"><TeamManage /></MenuRoute>} />
            <Route path="growth-methods" element={<MenuRoute menuKey="/growth-methods"><GrowthMethodManage /></MenuRoute>} />
            <Route path="supplier-prices" element={<MenuRoute menuKey="/supplier-prices"><SupplierPriceManage /></MenuRoute>} />
            <Route path="fan-monitor" element={<MenuRoute menuKey="/fan-monitor"><FanMonitor /></MenuRoute>} />
            <Route path="scheduled-tasks" element={<MenuRoute menuKey="/scheduled-tasks"><ScheduledTasks /></MenuRoute>} />
            <Route path="platforms" element={<MenuRoute menuKey="/platforms"><PlatformManage /></MenuRoute>} />
            <Route path="operation-logs" element={<MenuRoute menuKey="/operation-logs"><OperationLog /></MenuRoute>} />
            <Route path="yfx" element={<MenuRoute menuKey="/yfx"><EffectiveFans /></MenuRoute>} />
            <Route path="profile" element={<Profile />} />
            <Route path="dashboard" element={<MenuRoute menuKey="/dashboard"><Dashboard /></MenuRoute>} />
            <Route path="daily-dashboard" element={<MenuRoute menuKey="/daily-dashboard"><DailyDashboard /></MenuRoute>} />
            <Route path="settlement/supplier" element={<MenuRoute menuKey="/settlement/supplier"><SettlementSupplier /></MenuRoute>} />
            <Route path="settlement/overall" element={<MenuRoute menuKey="/settlement/overall"><SettlementOverall /></MenuRoute>} />
            <Route path="settlement/profit" element={<MenuRoute menuKey="/settlement/profit"><SettlementProfit /></MenuRoute>} />
            <Route path="settlement/bill-invoice" element={<MenuRoute menuKey="/settlement/bill-invoice"><BillInvoice /></MenuRoute>} />
            <Route path="api-config" element={<MenuRoute menuKey="/api-config"><ApiConfig /></MenuRoute>} />
            <Route path="api-call-logs" element={<MenuRoute menuKey="/api-call-logs"><ApiCallLogs /></MenuRoute>} />
            <Route path="api-open/endpoints" element={<MenuRoute menuKey="/api-open/endpoints"><ApiOpenEndpoints /></MenuRoute>} />
            <Route path="api-open/keys" element={<MenuRoute menuKey="/api-open/keys"><ApiOpenKeys /></MenuRoute>} />
            <Route path="api-open/logs" element={<MenuRoute menuKey="/api-open/logs"><ApiOpenLogs /></MenuRoute>} />
            <Route path="api-strategies" element={<MenuRoute menuKey="/api-strategies"><ApiStrategy /></MenuRoute>} />
            <Route path="api-orders/new" element={<MenuRoute menuKey="/api-orders/new"><ApiOrder /></MenuRoute>} />
            <Route path="api-orders" element={<MenuRoute menuKey="/api-orders"><ApiOrderList /></MenuRoute>} />
          </Route>
        </Routes>
      </BrowserRouter>
    </ConfigProvider>
  );
}

export default App;
